import RoundedCard from "@cle/components/RoundedCard";
import CleTable from "@cle/components/CleTable";
import type { SelectableEvidenceRow } from "@cle/models/selectableEvidenceRow";
import { getReportSubmissionTableColumns } from "@cle/utils/reportSubmissionTableColumns";
import type { SelectableReportStepKey } from "./RoundedReportOverview";
import { formatLessons } from "@cle/utils/lessonLimits";

export type { SelectableEvidenceRow } from "@cle/models/selectableEvidenceRow";

type ReportSubmissionCardProps = {
  description: string;
  disableReportedSelection?: boolean;
  documents: SelectableEvidenceRow[];
  errorMessage?: string;
  evidenceStepKey: SelectableReportStepKey;
  isError?: boolean;
  isLoading?: boolean;
  lessonsLabel?: string;
  /** Kategoriloftet for dette trin. Udeladt = ingen visning (den almindelige periode). */
  maxLessons?: number;
  /** Informativ besked (blå). Til overskud, der overføres og altså ikke går tabt. */
  noticeMessage?: string | null;
  selectedEvidenceItemIds: string[];
  selectedLessons: number;
  selectionLocked?: boolean;
  showTransferredFromPrevious?: boolean;
  title: string;
  transferredFromPreviousLessons?: number;
  warningMessage?: string | null;
  onSelectEvidenceItems: (evidenceItemIds: string[]) => void;
  onToggleEvidenceItem: (evidenceItemId: string) => void;
};

const ReportSubmissionCard = ({
  description,
  disableReportedSelection = true,
  documents,
  errorMessage,
  evidenceStepKey,
  isError = false,
  isLoading = false,
  lessonsLabel = "Antal lektioner",
  maxLessons,
  noticeMessage = null,
  onSelectEvidenceItems,
  onToggleEvidenceItem,
  selectedEvidenceItemIds,
  selectedLessons,
  selectionLocked = false,
  showTransferredFromPrevious = false,
  title,
  transferredFromPreviousLessons = 0,
  warningMessage = null,
}: ReportSubmissionCardProps) => {
  const columns = getReportSubmissionTableColumns(evidenceStepKey, documents);
  const handleToggleEvidenceItem = selectionLocked
    ? () => undefined
    : onToggleEvidenceItem;
  const handleSelectEvidenceItems = selectionLocked
    ? () => undefined
    : onSelectEvidenceItems;

  return (
    <RoundedCard bodyClassName="p-0">
      <div className="cle-report-detail__submission-panel">
        <div className="cle-report-detail__submission-header">
          <div>
            <p className="fw-semibold mb-0">{title}</p>
            <p className="text-muted small mb-0">{description}</p>
          </div>
          <p className="fw-semibold small mb-0">
            {lessonsLabel}:{" "}
            <span className="text-primary">
              {formatLessons(selectedLessons)}
            </span>
            {maxLessons !== undefined ? (
              <span className="text-muted fw-normal">
                {" "}
                af maks. {maxLessons}
              </span>
            ) : null}
          </p>
        </div>
        {showTransferredFromPrevious ? (
          <div className="cle-report-detail__submission-meta ">
            <span className="text-muted small d-flex align-items-center gap-2">
              Overført fra sidste periode:{" "}
              <span className="fw-semibold text-dark">
                {transferredFromPreviousLessons}
              </span>
            </span>
          </div>
        ) : null}

        {noticeMessage ? (
          <div className="alert alert-info mx-3 mt-3 mb-0" role="status">
            {noticeMessage}
          </div>
        ) : null}

        {warningMessage ? (
          <div className="alert alert-warning mx-3 mt-3 mb-0" role="alert">
            {warningMessage}
          </div>
        ) : null}

        <CleTable
          columns={columns}
          data={documents}
          emptyDescription="Når der er dokumenter, vises de her i oversigten."
          emptyTitle="Ingen dokumenter fundet"
          errorMessage={errorMessage}
          isError={isError}
          isLoading={isLoading}
          selection={{
            getAriaLabel: (document) => `Vælg ${document.fileName}`,
            isDisabled: selectionLocked
              ? () => true
              : disableReportedSelection
                ? (document) => document.reported === true
                : undefined,
            onSelectAll: handleSelectEvidenceItems,
            onToggle: handleToggleEvidenceItem,
            selectedIds: selectedEvidenceItemIds,
            selectAllAriaLabel: "Vælg alle dokumenter",
          }}
        />

        <div className="cle-report-detail__submission-footer">
          <span className="text-muted small">
            {selectedEvidenceItemIds.length} af {documents.length} dokumenter
            valgt{selectionLocked ? " (automatisk)" : ""}
          </span>
          <span className="fw-semibold small">
            {formatLessons(selectedLessons)} lektioner valgt
          </span>
        </div>
      </div>
    </RoundedCard>
  );
};

export default ReportSubmissionCard;
